import React from 'react'
import { useTranslation } from 'react-i18next'
import { FileQuestion, Plus, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { ChangeFilterState } from '../lib/changes'
import { CHANGE_KINDS, CHANGE_KIND_KEY, CHANGE_STATUSES, CHANGE_STATUS_KEY } from '../lib/labels'
import type { CostChangeKind, CostChangeStatus, CostContract } from '../types'

const ALL = 'all'
const TRIGGER = 'h-8 w-auto min-w-[150px] text-sm'

interface Props {
  count: number
  filter: ChangeFilterState
  onFilterChange: (filter: ChangeFilterState) => void
  contracts: CostContract[]
  canCreate: boolean
  onCreate: () => void
  onCreateFromRfq: () => void
}

const ChangesToolbar: React.FC<Props> = ({ count, filter, onFilterChange, contracts, canCreate, onCreate, onCreateFromRfq }) => {
  const { t } = useTranslation()
  const set = (patch: Partial<ChangeFilterState>) => onFilterChange({ ...filter, ...patch })
  return (
    <div className="flex flex-wrap items-center gap-3 py-2">
      <div className="flex items-center gap-1">
        <span className="text-lg font-bold">{count}</span>
        <span className="text-sm text-muted-foreground">{t('Common_total')}</span>
      </div>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        <Select value={filter.kind ?? ALL} onValueChange={(v) => set({ kind: v === ALL ? null : (v as CostChangeKind) })}>
          <SelectTrigger className={TRIGGER}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('CostControl_filter_all_change_kinds')}</SelectItem>
            {CHANGE_KINDS.map((k) => <SelectItem key={k} value={k}>{t(k === 'POTENTIAL' ? 'CostControl_filter_potential_changes' : CHANGE_KIND_KEY[k])}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.status ?? ALL} onValueChange={(v) => set({ status: v === ALL ? null : (v as CostChangeStatus) })}>
          <SelectTrigger className={TRIGGER}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('CostControl_filter_all_statuses')}</SelectItem>
            {CHANGE_STATUSES.map((s) => <SelectItem key={s} value={s}>{t(CHANGE_STATUS_KEY[s])}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.contractId ?? ALL} onValueChange={(v) => set({ contractId: v === ALL ? null : v })}>
          <SelectTrigger className="h-8 w-auto min-w-[180px] max-w-[260px] text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('CostControl_filter_all_contracts')}</SelectItem>
            {contracts.map((c) => <SelectItem key={c.id} value={c.id}>{c.number ? `${c.number} · ${c.name}` : c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={filter.search} onChange={(e) => set({ search: e.target.value })} placeholder={t('CostControl_changes_search')} className="h-8 w-[200px] pl-8 pr-8 text-sm" />
          {filter.search && (
            <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => set({ search: '' })} aria-label={t('CostControl_contracts_search_clear')}>
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      {canCreate && (
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-8" onClick={onCreateFromRfq}>
            <FileQuestion className="h-4 w-4" />
            {t('CostControl_change_from_rfq')}
          </Button>
          <Button size="sm" className="h-8" onClick={onCreate}>
            <Plus className="h-4 w-4" />
            {t('CostControl_change_new')}
          </Button>
        </div>
      )}
    </div>
  )
}
export default ChangesToolbar
